import React, { useEffect, useRef, useState } from 'react'
import { Logo } from '../components/Logo'
import ButtonBack from '../components/ButtonBack'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ToastTypesList } from '../utils/constants'
import copys, { termsAndPrivacy } from '../utils/copys'
import { getPrivacyPolicies, getTermsAndConditions } from '../services/commonService'
import { useToast } from '../hooks/useToast'
import parse from 'html-react-parser';
import * as gtmService from '../services/gtmService'
import * as Sentry from '@sentry/react'
import { TermsOrPrivacy } from '../types/common'
import { useRequest } from '../hooks/useRequest'
import Button from '../components/Button'
import { acceptNewTerms } from '../services/authService/authService'

type ContentWithId = {
    content: string,
    id: string,
}

const ScreenTermsAndPrivacy = () => {
    const params = useParams();
    const [search] = useSearchParams();
    const goTo = search.get('goto');
    const type = params.type as TermsOrPrivacy;
    const [content, setContent] = useState('');
    const { showToast } = useToast();
    const { requestWrapper } = useRequest();
    const [contents, setContents] = useState<ContentWithId[]>([]);
    const termsDivRef = useRef<HTMLDivElement>(null);
    const privacyDivRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();


    useEffect(() => {
        gtmService.pushPageView('terms_and_privacy')
        const getContent = async (contentType: TermsOrPrivacy) => {
            if (contentType !== 'both') {
                let response;
                if (contentType === 'terms') {
                    response = await requestWrapper(() => getTermsAndConditions());
                } else {
                    response = await requestWrapper(() => getPrivacyPolicies());
                }
                const { successful, data, errors } = response;
                if (successful) {
                    setContent(data.content);
                } else {
                    Sentry.captureException(errors, {
                        tags: {
                            flow: contentType === 'terms' ? 'load_app_terms' : 'load_app_privacy',
                        },
                    });
                    showToast(ToastTypesList.ERROR, copys.toastErrorMessageDefault);
                    console.log(errors);
                }
            } else {
                const [termsResponse, privacyResponse] = await Promise.all([
                    requestWrapper(() => getTermsAndConditions()),
                    requestWrapper(() => getPrivacyPolicies())
                ]);
                const { successful: termsSuccessful, data: termsData, errors: termsErrors } = termsResponse;
                const { successful: privacySuccessful, data: privacyData, errors: privacyErrors } = privacyResponse;
                if (termsSuccessful && privacySuccessful) {
                    setContents(
                        [termsData, privacyData].map((data) => ({
                            content: data.content,
                            id: data.id
                        }))
                    );
                } else {
                    Sentry.captureException({ termsErrors, privacyErrors }, {
                        tags: {
                            flow: 'load_app_terms_and_privacy',
                        },
                    });
                    showToast(ToastTypesList.ERROR, copys.toastErrorMessageDefault);
                    console.log({ termsErrors, privacyErrors });
                }
            }
        }

        getContent(type);
    }, [])

    useEffect(() => {
        if (contents.length > 0) {
            if (goTo === 'terms' && termsDivRef.current) {
                termsDivRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
            if(goTo === 'privacy' && privacyDivRef.current) {
                privacyDivRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
        }
    }, [contents]);

    const handleAccept = async() => {
        const responses = await Promise.all(
            contents.map(content => acceptNewTerms({
                public_content_id: content.id,
                isAccepted:true,
            })
            )
        );
        if (responses.some(r => !r.successful)) {
            showToast(ToastTypesList.ERROR, copys.toastAcceptNewTermsFailed);
        }else{
            navigate(-1);
        }
    }

    return (
        <div className='flex flex-col items-start'>
            <div className='py-3 px-4'>
                <Logo width={65} />
            </div>
            <ButtonBack />
            <div className="flex flex-col py-8 px-4 gap-4 text-text_strong">
                {
                    type !== 'both' && (
                        <>
                            <div className='font-libre-franklin text-3xl font-bold'>
                                {type === 'terms' ? termsAndPrivacy.termsFooterLabel : termsAndPrivacy.privacyFooterLabel}
                            </div>
                            <div className='font-roboto text-base font-normal '>
                                {parse(content)}
                            </div>
                        </>
                    )
                }
                {
                    type === 'both' && contents.length > 0 && (
                        <>
                            <div ref={termsDivRef} className='font-libre-franklin text-3xl font-bold'>
                                {termsAndPrivacy.termsFooterLabel}
                            </div>
                            <div className='font-roboto text-base font-normal '>
                                {parse(contents[0].content)}
                            </div>
                            <div ref={privacyDivRef} className='font-libre-franklin text-3xl font-bold'>
                                {termsAndPrivacy.privacyFooterLabel}
                            </div>
                            <div className='font-roboto text-base font-normal '>
                                {parse(contents[1].content)}
                            </div>
                            <div className='mt-2 flex justify-center items-center w-full'>
                                <Button label={termsAndPrivacy.modal.buttonAccept} enabled onClick={handleAccept} />
                            </div>
                        </>
                    )
                }
            </div>
        </div>
    )
}

export default ScreenTermsAndPrivacy
